import { isOnline } from '@/utils/network';
import { loadingStore } from '@/stores/loadingStore';
import { getStudy, getActiveStudy } from '@/config/studies';

class ACTApiService {
    static instance = null;

    constructor() {
        if (ACTApiService.instance) {
            return ACTApiService.instance;
        }
        ACTApiService.instance = this;
        
        this.baseUrl = process.env.NODE_ENV === 'production' 
            ? 'https://ucrbraingamecenter.org/api'
            : 'https://localhost:5001/api';
        this.actGroupId = null;
        this.authToken = null;
    }

    generateTaskId(method, endpoint) {
        return `${method}_${endpoint}_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`;
    }

    startApiLoading(message) {
        const taskId = this.generateTaskId('API', 'call');
        loadingStore.startLoading(taskId, message);
        return taskId;
    }

    stopApiLoading(taskId) {
        if (taskId) {
            loadingStore.stopLoading(taskId);
        }
    }

    setAuthToken(token) {
        this.authToken = token;
        sessionStorage.setItem('actApiAuthToken', token);
    }

    getAuthToken() {
        if (!this.authToken) {
            this.authToken = sessionStorage.getItem('actApiAuthToken');
        }
        return this.authToken;
    }

    async getTokenFromCognito() {
        if (!isOnline()) {
            console.warn('Cannot get token while offline');
            return null;
        }

        const taskId = this.startApiLoading('Getting authentication token...');

        try {
            const { CognitoUserPool } = await import('amazon-cognito-identity-js');
            
            const poolData = {
                UserPoolId: 'us-east-2_3novul0pN',
                ClientId: '51v76uda8o5l6niokhg852qnj5'
            };
            
            const userPool = new CognitoUserPool(poolData);
            const cognitoUser = userPool.getCurrentUser();
            
            if (!cognitoUser) {
                console.warn('No current user found');
                return null;
            }
            
            return new Promise((resolve, reject) => {
                cognitoUser.getSession((err, session) => {
                    if (err) {
                        console.error('Error getting session:', err);
                        reject(err);
                        return;
                    }
                    
                    if (session && session.isValid()) {
                        const token = session.getIdToken().getJwtToken();
                        this.setAuthToken(token);
                        resolve(token);
                    } else {
                        console.warn('Session is invalid or expired');
                        resolve(null);
                    }
                });
            });
        } catch (error) {
            console.error('Error getting token from Cognito:', error);
            return null;
        } finally {
            this.stopApiLoading(taskId);
        }
    }

    async fetchWithAuth(url, options = {}) {
        const token = this.getAuthToken();
        
        if (!token) {
            try {
                const newToken = await this.getTokenFromCognito();
                if (!newToken) {
                    throw new Error('Authentication token is not available. Please login first.');
                }
            } catch (error) {
                throw new Error('Failed to get authentication token: ' + error.message);
            }
        }

        if (!isOnline()) {
            throw new Error('Cannot make API requests while offline.');
        }

        const headers = {
            'Authorization': `Bearer ${this.getAuthToken()}`,
            'Content-Type': 'application/json',
            ...options.headers
        };

        return fetch(url, {
            ...options,
            headers
        });
    }

    async getLGroups(studyKey = null) {
        const study = studyKey ? getStudy(studyKey) : getActiveStudy();
        const taskId = this.startApiLoading('Loading participant groups...');
        
        try {
            let response = await this.fetchWithAuth(`${this.baseUrl}/ACTInterview/lgroups`);
            
            if (!response.ok) {
                throw new Error(`Failed to fetch LGroups: ${response.status} ${response.statusText}`);
            }
            
            let data = await response.json();
            
            // Only studies flagged autoCreateLGroup are created on demand. Preallocated
            // studies must already exist server side; there is no delete endpoint, so a
            // stray group created here would be unrecoverable.
            if (Array.isArray(data) && data.length === 0 && study.autoCreateLGroup) {
                loadingStore.setGlobalLoading(true, `Creating ${study.lGroupDisplayName} group...`);
                
                const createResponse = await this.fetchWithAuth(
                    `${this.baseUrl}/ACTInterview/lgroups`,
                    {
                        method: 'POST',
                        body: JSON.stringify({
                            lGroupName: study.lGroupDisplayName,
                            studyAcronym: study.studyAcronym,
                            initialVisitType: study.visitTypeName
                        })
                    }
                );

                if (!createResponse.ok) {
                    throw new Error(`Failed to create ${study.lGroupDisplayName} group: ${createResponse.status} ${createResponse.statusText}`);
                }

                // Make another GET request to fetch the updated groups
                response = await this.fetchWithAuth(`${this.baseUrl}/ACTInterview/lgroups`);
                
                if (!response.ok) {
                    throw new Error(`Failed to fetch LGroups after creation: ${response.status} ${response.statusText}`);
                }
                
                data = await response.json();
            }
            
            const lGroup = data.find(group => group.DisplayName === study.lGroupDisplayName);
            if (lGroup) {
                this.actGroupId = lGroup.LGroupId;
                localStorage.setItem('actGroupId', this.actGroupId);
                return this.actGroupId;
            } else {
                // Clear the cached id: callers fall back to it when passed a null lGroupId,
                // which would otherwise load the previous study's participants under this
                // study's selection.
                console.warn(`${study.lGroupDisplayName} group not found in the response`);
                this.actGroupId = null;
                localStorage.removeItem('actGroupId');
                return null;
            }
        } catch (error) {
            console.error('Error in getLGroups:', error);
            throw error;
        } finally {
            this.stopApiLoading(taskId);
        }
    }

    async getVisitTypes(lGroupId = null, studyKey = null) {
        const study = studyKey ? getStudy(studyKey) : getActiveStudy();
        if (!lGroupId) {
            lGroupId = this.getStoredActGroupId();
            if (!lGroupId) {
                throw new Error('LGroup ID is not available. Please fetch LGroups first.');
            }
        }

        const taskId = this.startApiLoading('Loading visit types...');

        try {
            const response = await this.fetchWithAuth(`${this.baseUrl}/ACTInterview/lgroups/${lGroupId}/visitTypes`);
            
            if (!response.ok) {
                throw new Error(`Failed to fetch visit types: ${response.status} ${response.statusText}`);
            }
            
            const visitTypes = await response.json();

            // Prefer the configured name, but fall back to the group's only visit type so a
            // study created with a different name still resolves.
            const visitType = visitTypes.find(type => type.VisitTypeName === study.visitTypeName)
                || visitTypes[0];

            if (visitType) {
                localStorage.setItem('biennialVisitTypeId', visitType.VisitTypeId);
                return visitType.VisitTypeId;
            } else {
                console.warn(`No visit types found for ${study.lGroupDisplayName}`);
                return null;
            }
        } catch (error) {
            console.error('Error fetching visit types:', error);
            throw error;
        } finally {
            this.stopApiLoading(taskId);
        }
    }

    async getParticipants(lGroupId = null) {
        if (!lGroupId) {
            lGroupId = this.getStoredActGroupId();
            if (!lGroupId) {
                throw new Error('LGroup ID is not available. Please fetch LGroups first.');
            }
        }

        const taskId = this.startApiLoading('Loading participants...');

        try {
            const response = await this.fetchWithAuth(`${this.baseUrl}/ACTInterview/lgroups/${lGroupId}/participants`);
            
            if (!response.ok) {
                throw new Error(`Failed to fetch participants: ${response.status} ${response.statusText}`);
            }
            
            const participants = await response.json();
            return participants;
        } catch (error) {
            console.error('Error fetching participants:', error);
            throw error;
        } finally {
            this.stopApiLoading(taskId);
        }
    }

    async getVisit(participantId, visitTypeId, showLoading = true) {
        const taskId = showLoading ? this.startApiLoading('Loading visit data...') : null;

        try {
            const response = await this.fetchWithAuth(`${this.baseUrl}/ACTInterview/participants/${participantId}/visits/${visitTypeId}`);

            if (!response.ok) {
                if (response.status === 500) {
                    const responseText = await response.text();
                    if (responseText === "Visit not found.") {
                        console.log('API returned 500 status but with expected "Visit not found" message - this is normal behavior');
                        return { notFound: true, error: responseText };
                    }
                    return { error: responseText };
                }
                throw new Error(`Failed to fetch visit: ${response.status} ${response.statusText}`);
            }

            const visitData = await response.json();
            return { success: true, data: visitData };
        } catch (error) {
            console.error('Error fetching visit:', error);
            return { error: error.message };
        } finally {
            if (showLoading) this.stopApiLoading(taskId);
        }
    }

    async createVisit(lGroupId, participantId, visitTypeId, serverCode, visitData = {}) {
        const requestBody = {
            lGroupID: lGroupId,
            participantID: participantId,
            visitTypeID: visitTypeId,
            serverCode: serverCode,
            visitData
        };

        console.log('Creating visit with data:', JSON.stringify(requestBody, null, 2));

        const taskId = this.startApiLoading('Creating visit...');

        try {
            const response = await this.fetchWithAuth(
                `${this.baseUrl}/ACTInterview/visits`, 
                {
                    method: 'POST',
                    body: JSON.stringify(requestBody)
                }
            );
            
            if (!response.ok) {
                const responseText = await response.text();
                
                // Special case: API returns 500 with "Visit saved successfully"
                if (response.status === 500 && responseText === "Visit saved successfully.") {
                    console.log('⚠️ API returned 500 status but with "Visit saved successfully" message - this appears to be normal API behavior');
                    // Not throwing error, just returning a success indicator
                    return { success: true, note: 'Visit created despite error status' };
                }
                
                console.warn(`Server returned ${response.status} error: ${responseText}`);
                throw new Error(`Server returned ${response.status}: ${responseText}`);
            }
            
            const visitData = await response.json();
            return visitData;
        } catch (error) {
            console.error('Error in createVisit request:', error);
            throw error;
        } finally {
            this.stopApiLoading(taskId);
        }
    }

    async updateVisitData(lGroupId, participantId, visitTypeId, serverCode) {
        await new Promise(resolve => setTimeout(resolve, 100));

        const participantDisplayId = serverCode;
        const subjectData = JSON.parse(localStorage.getItem('subjectData') || '{}');
        const participantData = subjectData[participantDisplayId] || {};

        const cleanedData = { ...participantData };

        // Remove duplicate verbal fluency data
        if (cleanedData.verbalFluencyLetterFData && cleanedData.letterFData) {
            delete cleanedData.letterFData;
            console.log('Removed duplicate letterFData from visit data');
        }

        if (cleanedData.verbalFluencySupermarketData && cleanedData.supermarketData) {
            delete cleanedData.supermarketData;
            console.log('Removed duplicate supermarketData from visit data');
        }

        // Fetch current server data so dashboard metadata (date, interviewer, QC) is not overwritten
        let serverVisitData = {};
        try {
            const current = await this.getVisit(participantDisplayId, visitTypeId, false);
            if (current?.success) {
                const raw = current.data?.VisitData || current.data?.visitData || current.data;
                if (raw) serverVisitData = raw;
            }
        } catch (e) {
            console.warn('Could not fetch current visit data before save; proceeding without merge:', e);
        }

        // Merge: server metadata is the base, local CASI data overwrites matching keys
        const mergedData = { ...serverVisitData, ...cleanedData };

        console.log(`Updating visit data for participant ${participantDisplayId}`, mergedData);

        const requestBody = {
            lGroupID: lGroupId,
            participantID: participantId,
            visitTypeID: visitTypeId,
            serverCode: serverCode,
            visitData: mergedData
        };
        
        const taskId = this.startApiLoading('Saving visit data...');
        
        try {
            const response = await this.fetchWithAuth(
                `${this.baseUrl}/ACTInterview/visits`, 
                {
                    method: 'POST',
                    body: JSON.stringify(requestBody)
                }
            );
            
            if (!response.ok) {
                const responseText = await response.text();
                console.warn(`Failed to update visit data. Server returned ${response.status}: ${responseText}`);
                throw new Error(`Failed to update visit data: ${response.status} ${responseText}`);
            }
            
            console.log('Visit data updated successfully');
            return await response.json();
        } catch (error) {
            console.error('Error updating visit data:', error);
            throw error;
        } finally {
            this.stopApiLoading(taskId);
        }
    }

    getStoredActGroupId() {
        return this.actGroupId || localStorage.getItem('actGroupId');
    }

    async getVisitLogs(visitId) {
        const taskId = this.startApiLoading('Loading visit logs...');

        try {
            const response = await this.fetchWithAuth(`${this.baseUrl}/ACTInterview/visits/${visitId}/logs`);
            
            if (!response.ok) {
                throw new Error(`Failed to fetch visit logs: ${response.status} ${response.statusText}`);
            }
            
            const logs = await response.json();
            return logs;
        } catch (error) {
            console.error('Error fetching visit logs:', error);
            throw error;
        } finally {
            this.stopApiLoading(taskId);
        }
    }

    async getLogBinary(logId) {
        const taskId = this.startApiLoading('Loading log file...');

        try {
            const response = await this.fetchWithAuth(`${this.baseUrl}/ACTInterview/logs/${logId}/binary`);
            
            if (!response.ok) {
                throw new Error(`Failed to fetch log binary: ${response.status}`);
            }
            
            return await response.blob();
        } catch (error) {
            console.error('Error fetching log binary:', error);
            throw error;
        } finally {
            this.stopApiLoading(taskId);
        }
    }

    async getLogTranscript(logId) {
        const taskId = this.startApiLoading('Loading transcript...');

        try {
            const response = await this.fetchWithAuth(`${this.baseUrl}/ACTInterview/logs/${logId}/transcript`);
            
            if (!response.ok) {
                throw new Error(`Failed to fetch transcript: ${response.status}`);
            }
            
            return await response.json();
        } catch (error) {
            console.error('Error fetching transcript:', error);
            throw error;
        } finally {
            this.stopApiLoading(taskId);
        }
    }

    // Comment methods - added for threaded comment system
    async getCommentsForLog(logId, showLoading = true) {
        const taskId = showLoading ? this.startApiLoading('Loading comments...') : null;

        try {
            const encodedLogId = encodeURIComponent(logId);
            const response = await this.fetchWithAuth(`${this.baseUrl}/ACTInterview/logs/${encodedLogId}/comments`);
            
            if (!response.ok) {
                const responseText = await response.text();
                if (response.status === 500) {
                    if (responseText.includes('Invalid log ID') || responseText.includes('not found')) {
                        // No comments yet, return empty array
                        return [];
                    }
                    console.warn('Error fetching comments (returning empty array):', responseText);
                    return [];
                }
                console.warn('Error fetching comments (returning empty array):', response.status, response.statusText);
                return [];
            }
            
            try {
                const comments = await response.json();
                return Array.isArray(comments) ? comments : [];
            } catch (parseError) {
                console.warn('Error parsing comments response (returning empty array):', parseError);
                return [];
            }
        } catch (error) {
            console.error('Error fetching comments:', error);
            // Return empty array on error so UI doesn't break
            return [];
        } finally {
            if (showLoading) this.stopApiLoading(taskId);
        }
    }

    /**
     * Creates a new comment. Comments are immutable; no updates.
     * To "edit", pass overrideId set to the ID of the comment being replaced.
     * @param {string} logId - Composite log ID (visitId|TASK|taskType)
     * @param {string|null} parentCommentId - For threading: ID of parent comment
     * @param {string|null} overrideId - When "editing": ID of comment this replaces
     * @param {string} commentText
     * @param {string} userData
     * @returns {Promise<{success: boolean, comment?: object}>}
     */
    async getPortalUsers() {
        const taskId = this.startApiLoading('Loading portal users...');
        try {
            const response = await this.fetchWithAuth(`${this.baseUrl}/userdata/PortalUsers`);
            if (!response.ok) {
                throw new Error(`Failed to fetch portal users: ${response.status} ${response.statusText}`);
            }
            return await response.json();
        } catch (error) {
            console.error('Error fetching portal users:', error);
            throw error;
        } finally {
            this.stopApiLoading(taskId);
        }
    }

    async getPortalUsersByRole(appName, accessRole) {
        const taskId = this.startApiLoading('Loading portal users...');
        try {
            const params = new URLSearchParams({ appName, accessRole });
            const response = await this.fetchWithAuth(`${this.baseUrl}/UserData/PortalUsers/ByRole?${params}`);
            if (!response.ok) {
                throw new Error(`Failed to fetch portal users by role: ${response.status} ${response.statusText}`);
            }
            return await response.json();
        } catch (error) {
            console.error('Error fetching portal users by role:', error);
            throw error;
        } finally {
            this.stopApiLoading(taskId);
        }
    }

    async getMyWorkflowTasks() {
        const taskId = this.startApiLoading('Loading workflow tasks...');
        try {
            const response = await this.fetchWithAuth(`${this.baseUrl}/ACTInterview/workflow/tasks/me`);
            if (!response.ok) {
                throw new Error(`Failed to fetch workflow tasks: ${response.status} ${response.statusText}`);
            }
            return await response.json();
        } catch (error) {
            console.error('Error fetching workflow tasks:', error);
            throw error;
        } finally {
            this.stopApiLoading(taskId);
        }
    }

    async createWorkflowTask(taskType, participantId, visitId, visitTypeId, targetPortalUserId) {
        const taskId = this.startApiLoading('Creating workflow task...');
        try {
            const response = await this.fetchWithAuth(
                `${this.baseUrl}/ACTInterview/workflow/tasks`,
                {
                    method: 'POST',
                    body: JSON.stringify({
                        taskType,
                        status: 'Assigned',
                        participantId,
                        visitId,
                        visitTypeId,
                        targetPortalUserId: parseInt(targetPortalUserId, 10),
                        targetType: 'User'
                    })
                }
            );
            if (!response.ok) {
                const text = await response.text();
                throw new Error(`Failed to create workflow task: ${response.status} ${text}`);
            }
            return await response.json();
        } catch (error) {
            console.error('Error creating workflow task:', error);
            throw error;
        } finally {
            this.stopApiLoading(taskId);
        }
    }

    async createComment(logId, parentCommentId, overrideId, commentText, userData) {
        const taskId = this.startApiLoading(overrideId ? 'Saving edit...' : 'Creating comment...');

        try {
            const requestBody = {
                logID: logId,
                commentText: commentText,
                userData: userData
            };
            if (parentCommentId) requestBody.parentCommentId = parentCommentId;
            if (overrideId) requestBody.overrideId = overrideId;

            const response = await this.fetchWithAuth(
                `${this.baseUrl}/ACTInterview/comments`,
                {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(requestBody)
                }
            );

            if (!response.ok) {
                const responseText = await response.text();
                console.error('Comment save failed:', response.status, responseText);
                throw new Error(`Failed to save comment: ${response.status} ${responseText}`);
            }

            const comment = await response.json();
            return { success: true, comment };
        } catch (error) {
            console.error('Error saving comment:', error);
            throw error;
        } finally {
            this.stopApiLoading(taskId);
        }
    }
}

export default ACTApiService; 